import { useState, useEffect, useCallback } from "react";
import {
  getAdminFeedback,
  createAdminFeedback,
  updateAdminFeedback,
  updateAdminFeedbackStatus,
} from "../../lib/api";
import { toList } from "../../lib/apiAdminUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const PRIORITIES = ["critical", "high", "medium", "low"];
const PRIORITY_LABELS = { critical: "Crítico", high: "Alto", medium: "Medio", low: "Bajo" };
const PRIORITY_COLORS = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-amber-100 text-amber-800 border-amber-200",
  medium: "bg-sky-100 text-sky-800 border-sky-200",
  low: "bg-slate-100 text-slate-800 border-slate-200",
};
const STATUSES = ["new", "reviewing", "in_progress", "resolved", "discarded"];
const STATUS_LABELS = {
  new: "Nuevo",
  reviewing: "Revisando",
  in_progress: "En progreso",
  resolved: "Resuelto",
  discarded: "Descartado",
};
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800",
  reviewing: "bg-violet-100 text-violet-800",
  in_progress: "bg-amber-100 text-amber-800",
  resolved: "bg-emerald-100 text-emerald-800",
  discarded: "bg-slate-100 text-slate-800",
};

function formatDate(s) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return s;
  }
}

const emptyFeedback = {
  user_name: "",
  contact_name: "",
  email: "",
  module: "",
  priority: "medium",
  status: "new",
  description: "",
  internal_notes: "",
};

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyFeedback);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (priorityFilter && priorityFilter !== "all") params.priority = priorityFilter;
      const res = await getAdminFeedback(params);
      setItems(toList(res?.data));
    } catch (err) {
      setItems([]);
      toast.error("Error al cargar feedback");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const openCreate = () => {
    setForm(emptyFeedback);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (fb) => {
    setForm({
      user_name: fb.user_name || "",
      contact_name: fb.contact_name || "",
      email: fb.email || "",
      module: fb.module || "",
      priority: fb.priority || "medium",
      status: fb.status || "new",
      description: fb.description || "",
      internal_notes: fb.internal_notes || "",
    });
    setEditingId(fb.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateAdminFeedback(editingId, form);
        toast.success("Feedback actualizado");
      } else {
        await createAdminFeedback(form);
        toast.success("Feedback creado");
      }
      setDialogOpen(false);
      fetchFeedback();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateAdminFeedbackStatus(id, status);
      toast.success("Estado actualizado");
      fetchFeedback();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const displayName = (fb) => fb.user_name || fb.contact_name || "-";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Feedback</h1>
          <p className="text-slate-500 mt-1">Comentarios y sugerencias de producto</p>
        </div>
        <Button onClick={openCreate} className="bg-slate-800 hover:bg-slate-700">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo feedback
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-4">
            <CardTitle className="text-lg">Lista de feedback</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Prioridad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {PRIORITY_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-slate-500">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500">No hay feedback</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Módulo</TableHead>
                  <TableHead>Prioridad</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((fb) => (
                  <TableRow key={fb.id}>
                    <TableCell className="font-medium">{displayName(fb)}</TableCell>
                    <TableCell>{fb.email || "-"}</TableCell>
                    <TableCell>{fb.module || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={PRIORITY_COLORS[fb.priority] || ""}>
                        {PRIORITY_LABELS[fb.priority] || fb.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_COLORS[fb.status] || ""}>
                        {STATUS_LABELS[fb.status] || fb.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(fb.created_at)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(fb)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          {STATUSES.filter((s) => s !== fb.status).map((s) => (
                            <DropdownMenuItem
                              key={s}
                              onClick={() => handleStatusChange(fb.id, s)}
                            >
                              Estado → {STATUS_LABELS[s]}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar feedback" : "Nuevo feedback"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nombre usuario</Label>
                <Input
                  value={form.user_name}
                  onChange={(e) => setForm({ ...form, user_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Nombre contacto</Label>
                <Input
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Módulo</Label>
                <Input
                  value={form.module}
                  onChange={(e) => setForm({ ...form, module: e.target.value })}
                  placeholder="p.ej. Consultas, EGI, Agenda"
                />
              </div>
              <div>
                <Label>Prioridad</Label>
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm({ ...form, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>
                        {PRIORITY_LABELS[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
              />
            </div>
            <div>
              <Label>Notas internas</Label>
              <Textarea
                value={form.internal_notes}
                onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-800 hover:bg-slate-700">
                {editingId ? "Guardar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
